/**
 * JSON-LD structured data generators for SEO.
 * Returns objects ready to be serialized as <script type="application/ld+json">.
 */

const SITE_URL = "https://setupsbyszese.com";

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "SZESE E-Sport",
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description:
      "Professional iRacing car setups by SZESE E-Sport, the sim racing team of Széchenyi István University, Győr, Hungary.",
    sameAs: [
      "https://www.instagram.com/szeseesport/",
      "https://www.facebook.com/esportszese/",
      "https://esport.sze.hu/en/",
    ],
    parentOrganization: {
      "@type": "CollegeOrUniversity",
      name: "Széchenyi István University",
      url: "https://sze.hu",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Győr",
        addressCountry: "HU",
      },
    },
  };
}

export function webSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Setups by SZESE",
    url: SITE_URL,
    description:
      "Download professional iRacing car setups crafted by world-class sim racers from SZESE E-Sport.",
    publisher: {
      "@type": "Organization",
      name: "SZESE E-Sport",
    },
  };
}

interface ProductTier {
  name: string;
  description: string;
  price: string;
  currency: string;
}

export function subscriptionProductsJsonLd() {
  const tiers: ProductTier[] = [
    {
      name: "Setups by SZESE — Plus",
      description: "All weekly iRacing setups for your chosen series. Fresh setups every week.",
      price: "8.00",
      currency: "USD",
    },
    {
      name: "Setups by SZESE — Premium",
      description: "All iRacing setups across every series, plus replay files and telemetry data.",
      price: "15.00",
      currency: "USD",
    },
  ];

  return tiers.map((tier) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: tier.name,
    description: tier.description,
    brand: {
      "@type": "Organization",
      name: "SZESE E-Sport",
    },
    offers: {
      "@type": "Offer",
      price: tier.price,
      priceCurrency: tier.currency,
      availability: "https://schema.org/InStock",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: tier.price,
        priceCurrency: tier.currency,
        billingDuration: {
          "@type": "QuantitativeValue",
          value: 1,
          unitCode: "MON",
        },
      },
    },
  }));
}

interface DriverPersonData {
  name: string;
  url: string;
  image?: string;
  description?: string;
  socialLinks?: Record<string, string>;
}

export function driverPersonJsonLd(driver: DriverPersonData) {
  const sameAs = driver.socialLinks ? Object.values(driver.socialLinks).filter(Boolean) : [];

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: driver.name,
    url: driver.url,
    ...(driver.image && { image: driver.image }),
    ...(driver.description && { description: driver.description }),
    ...(sameAs.length > 0 && { sameAs }),
    affiliation: {
      "@type": "Organization",
      name: "SZESE E-Sport",
      url: SITE_URL,
    },
    jobTitle: "Sim Racing Driver",
  };
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

export function faqJsonLd(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
